// File: src/pages/ViewRolePermissions.jsx
import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Label, Input } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";

// API & config (use your existing paths)
import { Get_Role_byid } from "../import/ApiEndPoint/ApiCalling";
import { Roles, NormalRoles } from "../import/AddRole";

/* ===== page-scoped CSS (same look as Create/Edit) ===== */
const injectOnce = () => {
  if (document.getElementById("viewrole-inline-css")) return;
  const s = document.createElement("style");
  s.id = "viewrole-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .grp-row{ border:1px solid #e5e7eb; border-radius:10px; overflow:hidden; }
    .grp-head{ display:flex; align-items:center; gap:10px; padding:10px 12px; background:#f4f7f6; cursor:pointer; }
    .grp-head:hover{ background:#f1f5f9; }
    .grp-title{ font-weight:700; }
    .pill{ border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; background:#fff; }
    .perm-row{ display:flex; align-items:center; gap:8px; padding:8px 12px; border-top:1px solid #f1f5f9; }
    .perm-name{ flex:2; text-align:left; font-weight:600; }
    .perm-col{ flex:1; display:flex; justify-content:center; }
    .toggle{ min-width:20px; font-weight:800; user-select:none; }
  `;
  document.head.appendChild(s);
};

const ALL_COLS_MASTER = [
  "View",
  "Create",
  "Edit",
  "Delete",
  "Download",
  "BulkUpload",
];
const ALL_COLS_SUPER = ["View", "Create", "Edit", "Delete"];

/** Build a map of viewer's allowed perms: tab -> Set(perms) */
const buildAllowMap = (rolePermission = []) => {
  const map = new Map();
  (rolePermission || []).forEach(({ pagename, permission }) => {
    if (!pagename) return;
    map.set(pagename, new Set(Array.isArray(permission) ? permission : []));
  });
  return map;
};

/** Restrict matrix (Roles/NormalRoles) to viewer’s allowed tabs/perms only */
const restrictByViewer = (matrix, allowMap) => {
  const out = [];
  (matrix || []).forEach((group) => {
    const keptTabs = [];
    (group?.TabName || []).forEach((tab) => {
      const allowed = allowMap.get(tab?.title);
      if (!allowed) return; // viewer can't see this tab at all
      // keep only perms that are explicitly allowed to the viewer (ignore "NA")
      const filteredPerms = (tab?.permission || []).filter(
        (p) => p !== "NA" && allowed.has(p)
      );
      if (filteredPerms.length) {
        keptTabs.push({ ...tab, permission: filteredPerms });
      }
    });
    if (keptTabs.length) {
      out.push({ ...group, TabName: keptTabs });
    }
  });
  return out;
};

export default function ViewRolePermissions() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);

  const [roleName, setRoleName] = useState("");
  const [roleDesc, setRoleDesc] = useState("");

  const [isMasterViewer, setIsMasterViewer] = useState(false);
  const [groups, setGroups] = useState([]); // filtered matrix the VIEWER can see
  const [expanded, setExpanded] = useState({}); // { [gi]: boolean }
  const [viewedMap, setViewedMap] = useState(new Map()); // viewed role perms: tab -> Set(perms)

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    (async () => {
      try {
        const viewer = JSON.parse(localStorage.getItem("userData") || "{}");
        const viewerIsMaster = viewer?.rolename?.roleName === "MASTER";
        setIsMasterViewer(viewerIsMaster);

        // Base matrix for viewer
        const base = viewerIsMaster ? Roles || [] : NormalRoles || [];
        const allowMap = buildAllowMap(viewer?.rolename?.rolePermission || []);
        const visibleMatrix = viewerIsMaster
          ? base
          : restrictByViewer(base, allowMap);

        // Load the ROLE we are viewing (same API as edit)
        const res = await Get_Role_byid(id);
        const role = res?.Role || {};
        setRoleName(role?.roleName || "");
        setRoleDesc(role?.desc || "");

        // map: tab -> Set(perms) for the viewed role
        const map = new Map();
        (role?.rolePermission || []).forEach(({ pagename, permission }) => {
          if (!pagename) return;
          map.set(
            pagename,
            new Set(Array.isArray(permission) ? permission : [])
          );
        });
        setViewedMap(map);

        // Now prune UI to show ONLY the boxes the viewed role actually has:
        //  1) remove tabs where viewed role has zero perms (for viewer-visible tabs)
        //  2) drop columns that have no checks anywhere
        const tabsWithChecks = visibleMatrix
          .map((g) => {
            const t = (g?.TabName || []).filter((tab) => {
              const set = map.get(tab?.title);
              return set && set.size > 0;
            });
            return { ...g, TabName: t };
          })
          .filter((g) => g.TabName.length > 0);

        setGroups(tabsWithChecks);
      } catch (e) {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const toggleGroup = (i) => setExpanded((p) => ({ ...p, [i]: !p[i] }));

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600 }}>Loading...</div>
      </div>
    );
  }

  // Decide the possible column labels (by viewer type)
  const baseCols = isMasterViewer ? ALL_COLS_MASTER : ALL_COLS_SUPER;

  // Compute which columns actually have ANY checked boxes for the viewed role
  const visibleCols = (() => {
    const set = new Set();
    groups.forEach((g) =>
      (g.TabName || []).forEach((tab) => {
        const checked = viewedMap.get(tab.title) || new Set();
        baseCols.forEach((label) => {
          if (checked.has(label)) set.add(label);
        });
      })
    );
    return baseCols.filter((label) => set.has(label));
  })();

  const nothingToShow = !groups.length || !visibleCols.length;

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                View Role <span className="count-pill">{roleName || "-"}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Details (read-only) ===== */}
      <Row>
        <Col xl={12}>
          <Card className="p-card" style={{ padding: 14 }}>
            <Row style={{ gap: 12 }}>
              <Col md="4" sm="12">
                <Label>Role Name</Label>
                <Input value={roleName} readOnly disabled />
              </Col>
              <Col md="6" sm="12">
                <Label>Role Description</Label>
                <Input value={roleDesc} readOnly disabled />
              </Col>
            </Row>

            {/* ===== Permissions (ONLY checked boxes) ===== */}
            <div style={{ marginTop: 14 }}>
              {nothingToShow ? (
                <div style={{ padding: 8, color: "#6b7280" }}>
                  No visible permissions for this role.
                </div>
              ) : (
                groups.map((group, gi) => {
                  const isOpen = !!expanded[gi];

                  // Count of checked boxes in this group (for pill)
                  let checkCount = 0;
                  group.TabName.forEach((tab) => {
                    const checked = viewedMap.get(tab.title) || new Set();
                    visibleCols.forEach((label) => {
                      if (checked.has(label)) checkCount += 1;
                    });
                  });

                  return (
                    <div
                      key={gi}
                      className="grp-row"
                      style={{ marginBottom: 10 }}
                    >
                      <div className="grp-head" onClick={() => toggleGroup(gi)}>
                        <span className="toggle">{isOpen ? "▾" : "▸"}</span>
                        <span className="grp-title">{group?.title || "-"}</span>
                        <span className="pill">
                          {(group?.TabName || []).length} screens / {checkCount}{" "}
                          checks
                        </span>
                      </div>

                      {isOpen && (
                        <div>
                          {/* Header row with ONLY columns that have checks */}
                          <div
                            className="perm-row"
                            style={{ background: "#fafafa", fontWeight: 700 }}
                          >
                            <div className="perm-name">Screen</div>
                            {visibleCols.map((label) => (
                              <div key={label} className="perm-col">
                                {label}
                              </div>
                            ))}
                          </div>

                          {(group?.TabName || []).map((tab, ti) => {
                            const tabTitle = tab?.title;
                            const tabChecked =
                              viewedMap.get(tabTitle) || new Set();

                            // If this tab has 0 checks in visibleCols, skip it
                            const hasAny = visibleCols.some((label) =>
                              tabChecked.has(label)
                            );
                            if (!hasAny) return null;

                            return (
                              <div key={ti} className="perm-row">
                                <div className="perm-name">{tabTitle}</div>
                                {visibleCols.map((label) => (
                                  <div key={label} className="perm-col">
                                    {tabChecked.has(label) ? (
                                      <Input
                                        type="checkbox"
                                        checked
                                        readOnly
                                        disabled
                                        style={{ width: 18, height: 18 }}
                                      />
                                    ) : (
                                      // empty cell when the role doesn't have that specific permission
                                      <span />
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
